import { useHistory } from "react-router-dom";
import { Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import { SiSteam, SiRiotgames, SiEpicgames } from "react-icons/si";
import { useEffect } from "react";
import { useRecoilValue } from "recoil";

import { analytics } from "MVVM/Analytics";
import { Button } from "MVVM/Components/Button";
import { IUnclaimedProfile } from "MVVM/@types";
import { getProfilePlatformType } from "MVVM/Utilities/User";
import { ProfileFirstClipUrlAtom } from "MVVM/State/profile";

interface IProPlayerUpsellProps {
  user: IUnclaimedProfile;
  onClose?: () => void;
}

export const ProPlayerUpsell = ({ user, onClose }: IProPlayerUpsellProps) => {
  const history = useHistory();
  const clipUrl = useRecoilValue(ProfileFirstClipUrlAtom);
  const clipCount = user.clipCount || 0;
  const displayClipCount = clipCount >= 99999 ? "99999+" : clipCount;
  const proPlayer = user.proPlayer;
  const platformType = getProfilePlatformType(user.playedGames);

  const backgroundImageUrl = clipUrl
    ? clipUrl
    : proPlayer?.team.logo
    ? proPlayer.team.logo
    : `${process.env.REACT_APP_S3_CLIP_BUCKET}/static/profile/upsell-modal/banner.png`;

  const avatarUrl = proPlayer?.player.avatar || user.avatarUrl;

  useEffect(() => {
    analytics.track("Claim Profile Upsell", {
      action: "display",
      username: user.username,
      clipCount,
      platform: platformType,
      isProPlayer: true,
    });
  }, [user.username, clipCount, platformType]);

  const handleSignUp = () => {
    analytics.track("Claim Profile Upsell - Claim Click", {
      username: user.username,
      platform: platformType,
      clipCount,
    });

    const currentPath = window.location.pathname + window.location.search;
    history.push(`/register?redirect=${encodeURIComponent(currentPath)}`);
  };

  const handleNoThanks = () => {
    analytics.track("Claim Profile Upsell - No Thanks Click", {
      username: user.username,
      platform: platformType,
    });

    onClose?.();
  };

  const getPlatformIcon = () => {
    switch (platformType) {
      case "Steam":
        return <SiSteam size={18} />;
      case "Riot":
        return <SiRiotgames size={18} />;
      case "Epic":
        return <SiEpicgames size={18} />;
      default:
        return null;
    }
  };

  const getPlatformName = () => {
    return platformType || "your account";
  };

  return (
    <Container>
      <ImageBanner backgroundImageUrl={backgroundImageUrl}>
        {clipUrl && (
          <>
            <BackgroundVideo autoPlay loop muted playsInline>
              <source src={clipUrl} type="video/mp4" />
            </BackgroundVideo>
            <VideoOverlay />
          </>
        )}
        <ProPlayerOverlay>
          {avatarUrl && (
            <Avatar
              src={avatarUrl}
              alt={proPlayer?.player.name || user.username}
            />
          )}
          <ProPlayerName>
            {proPlayer?.player.name || user.username}
          </ProPlayerName>
        </ProPlayerOverlay>
      </ImageBanner>
      <ContentSection>
        <Flex flexDirection="column" textAlign="center" style={{ gap: "16px" }}>
          <Title color="ice" variant="text.titleSmall">
            Create Pro-level Clips Like {user.username}
          </Title>
          <Subtitle color="chalk" variant="text.paragraphMedium">
            {user.username} has created{" "}
            <HighlightText as="span">{displayClipCount}</HighlightText>{" "}
            {clipCount === 1 ? "clip" : "clips"}. Sign up now to get your{" "}
            <HighlightText as="span">automated clips</HighlightText> and never
            miss a moment!
          </Subtitle>
        </Flex>
        {platformType === "Steam" ? (
          <ClaimButtonSteam size="small" onClick={handleSignUp}>
            <Flex
              alignItems="center"
              justifyContent="center"
              style={{ gap: "8px" }}
            >
              {getPlatformIcon()}
              Sign Up with {getPlatformName()}
            </Flex>
          </ClaimButtonSteam>
        ) : platformType === "Riot" ? (
          <ClaimButtonRiot size="small" onClick={handleSignUp}>
            <Flex
              alignItems="center"
              justifyContent="center"
              style={{ gap: "8px" }}
            >
              {getPlatformIcon()}
              Sign up with {getPlatformName()}
            </Flex>
          </ClaimButtonRiot>
        ) : platformType === "Epic" ? (
          <ClaimButtonEpic size="small" onClick={handleSignUp}>
            <Flex
              alignItems="center"
              justifyContent="center"
              style={{ gap: "8px" }}
            >
              {getPlatformIcon()}
              Sign up with {getPlatformName()}
            </Flex>
          </ClaimButtonEpic>
        ) : (
          <PrimaryButton size="small" onClick={handleSignUp}>
            <Flex
              alignItems="center"
              justifyContent="center"
              style={{ gap: "8px" }}
            >
              {getPlatformIcon()}
              Sign up with {getPlatformName()}
            </Flex>
          </PrimaryButton>
        )}
        <SecondaryButton onClick={handleNoThanks}>
          <Text variant={"text.labelMedium"} color="chalk">
            No Thanks
          </Text>
        </SecondaryButton>
      </ContentSection>
    </Container>
  );
};

const Container = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.ned};
  border-radius: 14px;
  border: ${({ theme }) => `1px solid ${theme.colors.envyAlpha20}`};
  box-shadow: ${({ theme }) => `0px 0px 16px 0px ${theme.colors.envyAlpha20}`};
  flex-direction: column;
  position: relative;
  text-align: center;
  width: 448px;

  @media (max-width: 500px) {
    margin: 20px;
    width: 390px;
  }

  @media (max-width: 425px) {
    width: calc(100vw - 40px);
  }
`;

const ImageBanner = styled(Flex)<{
  backgroundImageUrl: string;
}>`
  align-items: center;
  background-image: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.3) 0%,
      rgba(0, 0, 0, 0.5) 50%,
      var(--Gray-750, #1c2a34) 100%
    ),
    url(${({ backgroundImageUrl }) => backgroundImageUrl});
  background-position: center;
  background-size: cover;
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;
  height: 192px;
  justify-content: center;
  padding-bottom: 16px;
  position: relative;
  width: 100%;
  overflow: hidden;
`;

const BackgroundVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  filter: blur(2px);
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.4) 0%,
    rgba(0, 0, 0, 0.6) 50%,
    var(--Gray-750, #1c2a34) 100%
  );
  z-index: 0;
`;

const ProPlayerOverlay = styled(Flex)`
  align-items: center;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  z-index: 1;
`;

const Avatar = styled.img`
  border-radius: 50%;
  border: 4px solid ${({ theme }) => theme.colors.navy};
  height: 96px;
  width: 96px;
  object-fit: cover;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.5);
`;

const ProPlayerName = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 20px;
  font-weight: 700;
  line-height: 26px;
  text-align: center;
  text-shadow: 0px 2px 8px rgba(0, 0, 0, 0.6);
`;

const ContentSection = styled(Flex)`
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`;

const Title = styled(Text)`
  @media (max-width: 425px) {
    font-size: 24px;
    line-height: 28px;
  }
`;

const Subtitle = styled(Text)`
  @media (max-width: 425px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: ${({ theme }) => `${theme.colors.envy}20`};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.envy};
  color: ${({ theme }) => theme.colors.envy};
  min-width: 160px;
  transition: background-color 0.1s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.envy};
    color: var(--Ice-100, var(--colors-ice-500100, #f7fcfd));
  }
`;

const ClaimButtonSteam = styled(Button)`
  background: linear-gradient(
      0deg,
      ${({ theme }) => theme.colors.steamBlueAlpha20} 0%,
      ${({ theme }) => theme.colors.steamBlueAlpha20} 100%
    ),
    ${({ theme }) => theme.colors.steamBlueBg};
  border: 2px solid ${({ theme }) => theme.colors.steamBlue};
  border-radius: 8px;
  color: white;
  min-width: 160px;
  transition: background 0.1s ease;

  &:hover {
    background: linear-gradient(
        0deg,
        ${({ theme }) => theme.colors.steamBlueAlpha30} 0%,
        ${({ theme }) => theme.colors.steamBlueAlpha30} 100%
      ),
      ${({ theme }) => theme.colors.steamBlueBg};
  }
`;

const ClaimButtonRiot = styled(Button)`
  background: ${({ theme }) => theme.colors.riotRedAlpha20};
  border: 2px solid ${({ theme }) => theme.colors.riotRed};
  border-radius: 8px;
  color: white;
  min-width: 160px;
  transition: background 0.1s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.riotRedAlpha90};
  }
`;

const ClaimButtonEpic = styled(Button)`
  background: ${({ theme }) => theme.colors.epicGames};
  border: 2px solid ${({ theme }) => theme.colors.shade500};
  border-radius: 8px;
  color: white;
  min-width: 160px;
  transition: background 0.1s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.shade700};
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  transition: opacity 0.1s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const HighlightText = styled(Text)`
  color: ${({ theme }) => theme.colors.envy};
  font-weight: 700;
`;
